import parseTariff from '../personas/parseTariff'
import { DATE_KEY_PATTERN } from './formatDateKey'

/**
 * Controleert de invoer van een tarief en van een extra dagdeel vóórdat er iets
 * wordt weggeschreven. Een komma wordt als decimaalteken gelezen, want zo typt
 * men in Nederland een bedrag. Het bedrag zelf gaat via `parseTariff`, dezelfde
 * regel als het tariefveld van een traject (inbox #292: nooit `NaN` opslaan).
 *
 * AI-CONTEXT: See docs/pitfalls.md#state-or-fallback-erases-zero
 */


/** @param {*} value @returns {boolean} Of het een bestaande dag is als YYYY-MM-DD */
const isDateKey = (value) => {
    if (typeof value !== 'string') return false
    const match = DATE_KEY_PATTERN.exec(value)
    if (!match) return false

    const [year, month, day] = match.slice(1).map(Number)
    const date = new Date(Date.UTC(year, month - 1, day))
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

/**
 * @typedef {object} TariffValidation
 * @property {boolean} valid
 * @property {number|''} amount - Het bedrag als getal, `''` als het niet te lezen is
 * @property {string} startDate
 * @property {{amount?: string, startDate?: string}} errors - Melding per veld
 */

/**
 * @param {{amount?: string|number, startDate?: string}} input
 * @returns {TariffValidation}
 */
const validateTariff = ({ amount, startDate } = {}) => {
    const text = typeof amount === 'string' ? amount.replace(',', '.') : amount
    const parsed = parseTariff(text)
    const errors = {}

    if (parsed === '') errors.amount = 'Vul een bedrag in.'
    else if (parsed < 0) errors.amount = 'Een tarief kan niet negatief zijn.'

    if (!isDateKey(startDate)) errors.startDate = 'Kies een ingangsdatum.'

    return { valid: Object.keys(errors).length === 0, amount: parsed, startDate: startDate || '', errors }
}

/**
 * @typedef {object} AdjustmentValidation
 * @property {boolean} valid
 * @property {number} quantity - Het extra aantal, in de eenheid van het traject (dagdeel, uur of dag)
 * @property {string} reason - De reden, zonder spaties eromheen
 * @property {{quantity?: string, reason?: string}} errors - Melding per veld
 */

/**
 * @param {{quantity?: string|number, reason?: string}} input
 * @returns {AdjustmentValidation}
 */
const validateAdjustment = ({ quantity, reason } = {}) => {
    const count = typeof quantity === 'string' && quantity.trim() === '' ? NaN : Number(quantity)
    const cleanReason = typeof reason === 'string' ? reason.trim() : ''
    const errors = {}

    if (!Number.isInteger(count) || count < 1) errors.quantity = 'Vul een heel aantal van minstens 1 in.'
    if (cleanReason === '') errors.reason = 'Vul een reden in.'

    return { valid: Object.keys(errors).length === 0, quantity: count, reason: cleanReason, errors }
}

export default validateTariff
export { validateAdjustment }
